import React, {useEffect, useState, useCallback, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, useNavigate} from 'react-router-dom';
import { StudentStatisticsResult, StudentMonthlyReport } from '@PikaDoApp/npm-pikado-logic';
import moment from 'moment';

import {setSelectedStudent} from '../redux/actions/student';
import {Student} from '../graphql/API';
import {reducersState} from '../redux/reducers';
import {NewStudentReportService} from '../modules/Report/StudentReport/NewStudentReportService';
import {StudentReport} from '../modules/Report/StudentReport/types/NewStudentReportTypes';
import {StudentAttendanceChartContainer} from '../modules/Report/StudentReport/StudentAttendanceChartContainer';
import {StudentDailyPerformanceContainer} from '../modules/Report/StudentReport/StudentDailyPerformanceContainer';
import {StudentPerformanceChartsContainer} from '../modules/Report/StudentReport/StudentPerformanceChartsContainer';

// @ts-ignore: TS2459 — we know it's there at runtime
import {
  StudentStatsBar,
  MistakesSection,
} from '@PikaDoApp/pikado-web-dsm';
import SimpleLoader from '../components/SimpleLoader/SimpleLoader';
import {ReportHeader} from '../components/ReportHeader';
import {ReportSidebar} from '../components/ReportSidebar';
import {ReportFooter} from '../components/ReportFooter';
import AttendanceDashboardModal from '../modules/Report/StudentReport/AttendanceDashboardModal';


type StudentReportPageType = {
  student: Student;
};

const newStudentReportService = new NewStudentReportService();

export const StudentReportPage = ({student}: StudentReportPageType) => {
  // –––––––––––––– LOGIC / STATE ––––––––––––––
  const params = useParams() as {studentId: string};
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedItemData, setSelectedItemData] = useState<any>(null);
  const dispatch = useDispatch();
  const [months, setMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<{month: string; index: number} | null>(null);
  const [studentReports, setStudentReports] = useState<StudentReport[]>([]);
  const [studentStatistics, setStudentStatistics] = useState<StudentStatisticsResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(true);
  const [studentMonthlyReport,setStudentMonthlyReport] = useState<StudentMonthlyReport>()
  // Request management refs for cleanup and race condition prevention
  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
 const [monthOptions, setMonthOptions] = useState<Array<{label: string; value: string}>>([]);
  const selectedStudent: Student = useSelector(
    (state: any) => state.student?.selectedStudent,
  );
  const school = useSelector((state: reducersState) => state.user?.school);
  
  const selectedClassIdFromRedux = useSelector((state: reducersState) => state.classState?.selectedClassId);
  const students = useSelector((state: reducersState) => 
    state.classState?.classStudents?.[selectedClassIdFromRedux]
  );

  // Get the currently selected month's report
  const selectedMonthReport = useMemo(() => {
    if (!selectedMonth?.month || !studentReports.length) return undefined;
    return studentReports.find(
      (report) => moment(report.month).format('YYYY-MM') === selectedMonth.month
    );
  }, [studentReports, selectedMonth]);

  // Fetch student reports using new API with race condition prevention
  const fetchStudentReports = useCallback(async (
    studentId: string,
    requestId: number,
    signal: AbortSignal,
    monthToFetch: string
  ) => {
    try {
      console.log(`🔄 [Request ${requestId}] Fetching reports for student:`, studentId, 'for month:', monthToFetch);

     // const reports = await newStudentReportService.getAllStudentReports(studentId);
      const stats = await newStudentReportService.getStudentStatisticsByStudentId(studentId);
      const studentReport = await newStudentReportService.getStudentMonthlyReportByStudentIdAndMonth(studentId, monthToFetch);

      setStudentMonthlyReport(studentReport)
      setStudentStatistics(stats)

      // Check if this request is still relevant
      if (signal.aborted || requestIdRef.current !== requestId || !isMountedRef.current) {
        console.log(`❌ [Request ${requestId}] Aborted or stale`);
        return null;
      }

      console.log(`✅ [Request ${requestId}] Reports fetched successfully`);
      return { stats: stats[0] || null };

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log(`🛑 [Request ${requestId}] Request cancelled`);
        return null;
      }
      console.error(`❌ [Request ${requestId}] Error:`, error);
      throw error;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset state for new student
    setIsLoading(true);
    setStudentStatistics(null);
    setMonths([]);
    setSelectedMonth(null);

    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Increment request ID
    const currentRequestId = ++requestIdRef.current;
    if(!students)return
    const loadStudentData = async () => {
      try {
        // Reset to current month when switching students
        const currentMonthStr = moment().format('YYYY-MM');
        setSelectedMonth({
          month: currentMonthStr,
          index: 0,
        });

        const student = students?.find((std) => std.id === params.studentId);
        dispatch(setSelectedStudent(student));

        await fetchStudentReports(
          params.studentId,
          currentRequestId,
          abortController.signal,
          currentMonthStr
        );
        setIsLoading(false);

      } catch (error: any) {
        if (!isMountedRef.current || error.name === 'AbortError') {
          return;
        }
        console.error(`❌ [Request ${currentRequestId}] Error:`, error);
        setStudentReports([]);
        setStudentStatistics(null);
        setIsLoading(false);
      }
    };

    loadStudentData();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [params.studentId, dispatch,students]);

    // Transform pastMonthlyAverageList to rawClassReports format
  const rawClassReports = useMemo(() => {
    if (!studentStatistics?.studentPastMonthlyAverageList) return [];

    return studentStatistics?.studentPastMonthlyAverageList.map((item: { average: number; month: string }) => ({
      month: item.month,
      classMonthlyAverage: item.average,
      monthlyReports: [], 
    }));
  }, [studentStatistics?.studentPastMonthlyAverageList]);

  const generateMonthOptions = useMemo(() => {
    const currentMonth = moment().format('YYYY-MM');
    const optionMonths = new Set<string>();

    rawClassReports.forEach((report: any) => {
      if (report?.month) {
        optionMonths.add(moment(report.month).format('YYYY-MM'));
      }
    });

    optionMonths.add(currentMonth);

    if (selectedMonth?.month) {
      optionMonths.add(selectedMonth.month);
    }

    return Array.from(optionMonths)
      .sort((a, b) => moment(a).diff(moment(b)))
      .map((month) => ({
        label: moment(month).format('MMMM YYYY'),
        value: month,
      }));
  }, [rawClassReports, selectedMonth?.month]);
    // Sync month options to state
  useEffect(() => {
    setMonthOptions(generateMonthOptions);
  }, [generateMonthOptions]);


  // Handler for attendance day clicks
  const handleDayClick = useCallback((dayData: any) => {
    setSelectedItemData(dayData);
    setModalOpen(true);
  }, []);

  // Month change handler for synchronized month selection
  const handleMonthChange = useCallback(async(newMonth: string) => {
    setIsLoading(true)
  const StudentMR =   await newStudentReportService.getStudentMonthlyReportByStudentIdAndMonth(selectedStudent.id, newMonth);
  setStudentMonthlyReport(StudentMR)
    const monthIdx = monthOptions.findIndex((option) => option.value === newMonth);
    setSelectedMonth({
      month: newMonth,
      index: monthIdx >= 0 ? monthIdx : 0,
    });
    setIsLoading(false)
  }, [monthOptions, selectedStudent?.id]);

  // Report content to be displayed (no tabs, just the selected month)
  const reportContent = useMemo(() => {
    if (!selectedMonth?.month) return null;

    return (
      <main className="flex flex-col gap-8 p-4 md:p-6 lg:p-8">
        {/* Attendance Chart Container */}
        <StudentAttendanceChartContainer
        studentDailyPerformanceList={studentMonthlyReport?.studentDailyPerformanceList}
          studentMonthlyReport={studentMonthlyReport}
          selectedMonth={selectedMonth.month}
          onMonthChange={handleMonthChange}
          monthOptions={monthOptions}
          onDayClick={handleDayClick}
          interactive={true}
        />

        {/* Daily Performance Container */}
        <StudentDailyPerformanceContainer
          studentDailyPerformanceList={studentMonthlyReport?.studentDailyPerformanceList}
          selectedMonth={selectedMonth.month}
          onMonthChange={handleMonthChange}
          monthOptions={monthOptions}
        />

        {/* Performance Charts Container */}
        <StudentPerformanceChartsContainer
          monthlyAverage={
            Number.isFinite(studentMonthlyReport?.studentMonthlyAverage)
              ? Number((studentMonthlyReport?.studentMonthlyAverage ?? 0).toFixed(2))
              : 0
          }
          historicalPerformanceData={studentStatistics?.studentPastMonthlyAverageList}
          selectedMonth={selectedMonth.month}
          onMonthChange={handleMonthChange}
          monthOptions={monthOptions}
        />

        {/* Mistakes Section */}
        <MistakesSection
          syllables={studentMonthlyReport?.studentMonthlySyllables}
          phonetics={studentMonthlyReport?.studentMonthlyPhonetics}
          syllablesTitle="Syllable Mistakes"
          phoneticsTitle="Phonetic Mistakes"
        />

        {/* Footers */}
        <ReportFooter />

        {isLoading && (
          <SimpleLoader isLoading={true} title="Analysing your data. it may take a minute." />
        )}
      </main>
    );
  }, [
    selectedMonth,
    studentReports,
    studentStatistics,
    studentMonthlyReport,
    monthOptions,
    isLoading,
    handleDayClick,
    handleMonthChange,
  ]);

  // Get user for header
  const user = useSelector((state: any) => state.user.user);



  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <SimpleLoader isLoading={true} title="Analysing your data. it may take a minute." />
        </div>
      ) : (
        <div className="flex h-screen overflow-hidden bg-pikadoWhiteSmoke">
          {/* Sidebar */}
          <ReportSidebar
            isMobileOpen={isMobileSidebarOpen}
            onMobileClose={() => setIsMobileSidebarOpen(false)}
            isCollapsed={isDesktopSidebarCollapsed}
            onToggleCollapse={() => setIsDesktopSidebarCollapsed(!isDesktopSidebarCollapsed)}
          />

          {/* Main content area */}
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Sticky header + stats bar */}
            <div className="sticky top-0 z-30">
              <ReportHeader
                school={school}
                schoolName={school?.name || ''}
                studentCount={school?.students?.length || 0}
                userName={user?.firstName || ''}
                onMobileMenuClick={() => setIsMobileSidebarOpen(true)}
              />

              <StudentStatsBar
                  performancePercentage={Number(studentStatistics?.studentLifeTimeAverage.toFixed(2))}
                  classRank={studentMonthlyReport?.monthlyRankInClass}
                classStudents={students}
                selectedStudentId={selectedStudent?.id || params.studentId}
                onStudentChange={(studentId) => navigate(`/student/${studentId}`)}
                currentMonthReport={selectedMonthReport}
              />
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {reportContent}
            </div>
          </div>
        </div>
      )}
      <AttendanceDashboardModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        info={selectedItemData}
      />
    </>
  );
};
